import React, {useContext, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {AppCtx} from '../../../../AppCtx';
import {useSongContentService} from '../../hooks/useSongContentService';
import {SongsEmptySectionsRemover} from '../../services/songs-empty-sections-remover';
import PresentationSongComponent from './PresentationSongComponent';
import useScreenWakeLock from '../../../../shared/hooks/useScreenWakeLock';
import {setHosannaPresenceRole} from '../../../../infrastructure/sync/hosanna-presence-role';
import yjsDocumentService from '../../../../infrastructure/sync/YjsDocumentService';
import './PresentationDeviceScreen.css';

// A passive device follows only the canonical presentation target, never the
// browsing route or group selection. Mounting it must not publish anything.
export default function PresentationDeviceScreen() {
  const {sharedPresentationState: state, setIsPresentationMode} = useContext(AppCtx);
  const target = state?.canonicalTarget;
  const notation = target?.presentationChordNotation;
  const {songsJson} = useSongContentService(true, {name: state?.fileName},
    notation === 'pl' ? 'polish' : notation === 'us' ? 'international' : 'original', target);
  const contentRef = useRef(null);
  const viewportRef = useRef(null);
  const [scale, setScale] = useState(1);
  useScreenWakeLock(true);
  // This device is a screen, not a person, in Hosanna's presence counter.
  useEffect(() => {
    setHosannaPresenceRole(yjsDocumentService, 'screen');
    return () => setHosannaPresenceRole(yjsDocumentService, null);
  }, []);
  const song = songsJson?.songs.find(item => item.currentPosition === state?.songNumber)
    ?? (state?.songNumber == null ? songsJson?.songs.find(item => state?.line >= item.lineStart && state?.line <= item.lineEnd) : null);
  const sections = song?.sections.filter(section => SongsEmptySectionsRemover.isVisibleSection(section, state?.chordsVisible)) ?? [];
  const section = sections[target?.sectionIndex - 1]
    ?? sections.find(item => item.content.flat().some(line => line.lineNumber === state?.line));
  const blank = target?.presentationBlankScreen;
  const hasContent = !!song && (target?.kind !== 'section' || !!section);
  useLayoutEffect(() => {
    const fit = () => {
      const content = contentRef.current, viewport = viewportRef.current;
      if (!content || !viewport) return;
      setScale(Math.min(viewport.clientWidth / Math.max(1, content.scrollWidth),
        viewport.clientHeight / Math.max(1, content.scrollHeight)));
    };
    fit();
    // Web fonts and late-rendered song parts change the content size after the
    // first fit; observe the content too so the text never overflows the screen.
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
    if (viewportRef.current) observer?.observe(viewportRef.current);
    if (contentRef.current) observer?.observe(contentRef.current);
    document.fonts?.ready?.then(fit);
    window.addEventListener('resize', fit);
    const escape = event => {if (event.key === 'Escape') setIsPresentationMode(false);};
    window.addEventListener('keydown', escape);
    return () => {observer?.disconnect(); window.removeEventListener('resize', fit); window.removeEventListener('keydown', escape);};
  }, [songsJson, state, hasContent, blank, setIsPresentationMode]);
  const dark = target?.presentationTheme !== 'light';
  const font = target?.presentationFontFamily;
  return <section className="presentation-device-screen" aria-label="Ekran prezentacji"
    data-layout={target?.presentationLayoutMode ?? 'full'}
    style={{background: blank || (dark ? '#000' : '#fff'), color: dark ? '#fff' : '#000',
      padding: target?.presentationViewportPaddingPx ?? 16, '--text-color': dark ? '#fff' : '#000',
      '--presentation-font': font || 'inherit', '--presentation-columns': target?.presentationColumnCount ?? 1,
      '--presentation-chord-font': target?.presentationChordFontFamily || font || 'inherit',
      '--presentation-chord-size': `${target?.presentationChordZoomRatio ?? 0.7}em`,
      '--presentation-chord-weight': target?.presentationChordFontBold === false ? 'normal' : 'bold',
      '--presentation-chord-style': target?.presentationChordFontItalic ? 'italic' : 'normal'}}>
    <button className="presentation-device-exit" onClick={() => setIsPresentationMode(false)} aria-label="Zamknij ekran prezentacji">×</button>
    <div className="presentation-device-viewport" ref={viewportRef}>
      {blank && <span className="presentation-device-sr">Czarny ekran</span>}
      {!blank && !hasContent && <p className="presentation-device-waiting" role="status">Oczekiwanie na prezentację…
        <small>Esc lub × zamyka ten ekran</small></p>}
      {!blank && hasContent && <div ref={contentRef} className="presentation-device-content" style={{
        // A zoom above 1 would push text off the screen, so it can only shrink.
        transform: `scale(${scale * Math.min(target?.presentationZoomFactor ?? 1, 1)})`,
        fontFamily: font, fontWeight: target?.presentationFontBold ? 'bold' : undefined,
        fontStyle: target?.presentationFontItalic ? 'italic' : undefined,
        lineHeight: target?.presentationSpacingFactor ?? 1.3,
        columnCount: target?.presentationColumnCount ?? 1
      }}>
        <PresentationSongComponent song={{...song, sections: target?.kind === 'section' ? [section] : sections,
          title: target?.kind === 'section' ? '' : song.title}} chordsVisible={state?.chordsVisible} />
      </div>}
    </div>
  </section>;
}
